/**
 * Trunnion AI - Code Block Component
 * Syntax highlighted JSON/YAML viewer.
 */

import { useState } from 'react';
import { Copy, Check } from 'lucide-react';

interface CodeBlockProps {
  content: string;
  language?: 'json' | 'yaml' | 'text';
  title?: string;
  maxHeight?: string;
  className?: string;
}

export function CodeBlock({
  content,
  language = 'json',
  title,
  maxHeight = '400px',
  className = '',
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900 ${className}`}
    >
      {title && (
        <div className="flex items-center justify-between border-b border-trunnion-slate-700 px-3 py-2">
          <span className="text-xs font-medium text-trunnion-slate-400">
            {title}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-trunnion-slate-500 uppercase">
              {language}
            </span>
            <button
              onClick={handleCopy}
              className="rounded p-1 text-trunnion-slate-400 hover:bg-trunnion-slate-800 hover:text-white transition-colors"
              title="Copy to clipboard"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-400" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>
      )}
      <pre
        className="overflow-auto p-3 text-xs leading-relaxed text-trunnion-slate-200 font-mono"
        style={{ maxHeight }}
      >
        <code>{content}</code>
      </pre>
    </div>
  );
}
